/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: Custom Tree-Table
 * Reason: Custom PrimeNG p-treeTable for the 'Objekt-Zuordnung' tab. Deliberately
 *         NOT a WidgetFactory-based widget — the forensic tree needs heterogeneous
 *         levels (Record → Structure → Field / Conflict) with bespoke icons, labels,
 *         and status tags that the generic tree-table widget cannot express. Fetches
 *         all four levels through QL in a single batched builder call
 *         (`qlService.builder()`, one query per level, joined back to the import
 *         via explicit nested `joins[]` trees per QL skill rule QL5).
 */
import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {TreeNode} from 'primeng/api';
import {Subscription} from 'rxjs';

import {QlService} from '../../../../../../core/shared/ql/service/ql.service';
import {FilterCriteria} from '../../../../../../core/shared/filter/api/filter.criteria';

import {EiImportRecordStatusInternal} from '../../../../enum/ei-import-record-status-internal.enum';
import {EiImportRecordStructureAssignmentStatus} from '../../../../enum/ei-import-record-structure-assignment-status.enum';
import {EiImportRecordStructureFieldStatus} from '../../../../enum/ei-import-record-structure-field-status.enum';
import {EiConflictResolutionStatus} from '../../../../enum/ei-conflict-resolution-status.enum';

/**
 * Row payload emitted when a tree node is clicked. Steers the right-panel mode
 * on the parent page (record summary vs. candidate list vs. conflict form).
 *
 * `treeNode` carries the PrimeNG TreeNode reference for the clicked row so the
 * parent can hand the full in-memory subtree (children + grandchildren + …) to
 * the org-chart renderer without re-running the QL batch.
 */
export interface EiTreeNodeSelection {
    objectType:
        | 'ei.EiImportRecord'
        | 'ei.EiImportRecordStructure'
        | 'ei.EiImportRecordStructureField'
        | 'ei.EiImportRecordStructureFieldConflict';
    objectId: number;
    data: any;
    treeNode?: TreeNode<any>;
}

/** Shape of a single row in the rendered tree. */
interface TreeRow {
    /** Discriminator for the node type — drives icon/severity/emit payload. */
    kind: 'record' | 'structure' | 'field' | 'conflict';
    /** Entity id on the respective level. Used as `dataKey` (prefixed to avoid id collisions across levels). */
    nodeKey: string;
    /** The raw entity id (`id` column of the QL row). */
    id: number;
    /** Left icon (FontAwesome) — derived from kind. */
    icon: string;
    /** Primary label shown in the first content column. */
    label: string;
    /** Secondary label (objectType / attributeName / structure path) shown in the second column. */
    subLabel?: string;
    /** Raw status value (enum ordinal or string) used by the status column. */
    statusValue?: number | string;
    /** Human-readable status label (German). */
    statusLabel?: string;
    /** Tag severity token for <p-tag>. */
    statusSeverity?: 'success' | 'info' | 'warn' | 'danger' | 'secondary';
    /** Conflict/issue counters if available (records + structures). */
    conflictBadge?: string;
    /** The raw row data — forwarded to the emit payload. */
    raw: any;
}

@Component({
    selector: 'ei-import-record-tree-table',
    templateUrl: './ei-import-record-tree-table.component.html',
    standalone: false,
})
export class EiImportRecordTreeTableComponent implements OnChanges, OnDestroy {

    /** The import whose records + structures + fields + conflicts are rendered. */
    @Input() importId?: number;

    /**
     * Fires when a tree node (any level) is clicked. The page uses the objectType
     * + objectId to drive the right-hand panel of the splitter.
     */
    @Output() nodeSelect = new EventEmitter<EiTreeNodeSelection>();

    /** PrimeNG TreeNode tree bound to the template's p-treeTable. */
    treeNodes: TreeNode<TreeRow>[] = [];

    /** True while the QL batch is in flight. */
    loading = false;

    /** Last error message (if any) from the QL call. */
    errorMessage?: string;

    private querySub?: Subscription;

    constructor(private qlService: QlService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['importId']) {
            this.reload();
        }
    }

    ngOnDestroy(): void {
        this.querySub?.unsubscribe();
    }

    /**
     * Kick off the batched QL fetch and rebuild the tree. Called on input change
     * and can be invoked from the outside after a write-action refreshes server
     * state (e.g., candidate confirmed, conflict resolved).
     */
    public reload(): void {
        this.querySub?.unsubscribe();
        this.treeNodes = [];
        this.errorMessage = undefined;

        if (!this.importId) {
            return;
        }
        const importId = this.importId;

        this.loading = true;
        this.querySub = this.qlService.builder()
            /**
             * Level 1 — EiImportRecord rows for this import.
             * We deliberately omit `recordJson` (jsonb column) from the field
             * projection; the QL TypeConvertorService has no PGobject→String
             * mapping and the whole query fails if jsonb rows are materialised.
             */
            .query({
                name: 'records',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'ei.EiImportRecord',
                    as: 'e',
                    fields: [
                        {name: 'id'},
                        {name: 'status'},
                        {name: 'requiresManualReview'},
                        {name: 'conflictCount'},
                        {name: 'resolvedConflictCount'},
                    ],
                },
                filters: [
                    FilterCriteria.create('e.eiImportId', FilterCriteria.cOperatorEqual, importId),
                ],
            })
            /**
             * Level 2 — every EiImportRecordStructure belonging to a record of this
             * import. Reached via an inner join to `importRecord`, filtered on the
             * joined alias (`ir.eiImportId`) per QL rule QL5 (nested FK filters
             * need explicit join trees — no dotted-path shortcuts).
             *
             * We deliberately AVOID `resolve_fkey_texts` and instead add explicit
             * joins with chosen aliases for every FK we need to read from the QL
             * row. The `simple` pipe does not materialise FK fields under their
             * declared names (e.g. `e.importRecord*` is NOT in the response) —
             * only `e.<column>` for plain columns. The auto-resolver only adds
             * joins (named `fkeyt_e_<index>`) for FKs whose target entity exposes
             * `name`/`calculatedName`/`formatName`, and the index is positional
             * which makes it brittle. Explicit joins give us stable
             * `<alias>.id` / `<alias>.name` keys regardless of entity changes.
             */
            .query({
                name: 'structures',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'ei.EiImportRecordStructure',
                    as: 'e',
                    fields: [
                        {name: 'id'},
                        {name: 'objectId'},
                        {name: 'assignmentStatus'},
                        {name: 'conflictCount'},
                        {name: 'resolvedConflictCount'},
                    ],
                    joins: [
                        {
                            type: 'entity',
                            name: 'importRecord',
                            as: 'ir',
                            joinType: 'inner',
                            // Only fetch `id` to avoid PGobject→String conversion
                            // failure on `recordJson` (JSON column). Filter columns
                            // are added automatically by QlDpEntityJdbcMembersResolver.
                            fields: [{name: 'id'}],
                        },
                        {
                            type: 'entity',
                            name: 'parent',
                            as: 'pa',
                            joinType: 'left',
                            // For self-referential `parent` FK — used to nest
                            // sub-structures under their parent structure node.
                            fields: [{name: 'id'}],
                        },
                        {
                            type: 'entity',
                            name: 'structure',
                            as: 'st',
                            joinType: 'inner',
                            // EiStructure provides the human-readable structure name
                            // for the row's primary label.
                            fields: [{name: 'id'}, {name: 'name'}],
                        },
                        {
                            type: 'entity',
                            name: 'objectType',
                            as: 'ot',
                            joinType: 'left',
                            // ObjectType is nullable on EiImportRecordStructure;
                            // when present its `name` is shown as the row's
                            // secondary label.
                            fields: [{name: 'id'}, {name: 'name'}],
                        },
                    ],
                },
                filters: [
                    FilterCriteria.create('ir.eiImportId', FilterCriteria.cOperatorEqual, importId),
                ],
            })
            /**
             * Level 3 — EiImportRecordStructureField rows for every structure of
             * this import. Two-hop join: structure → record → import. Same
             * rationale as the structures query — explicit joins instead of
             * `resolve_fkey_texts`, so we can read FK ids and names from
             * stable alias-prefixed keys (`irs.id`, `sf.name`) rather than
             * positional `fkeyt_e_<index>` aliases.
             */
            .query({
                name: 'fields',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'ei.EiImportRecordStructureField',
                    as: 'e',
                    fields: [
                        {name: 'id'},
                        {name: 'status'},
                    ],
                    joins: [
                        {
                            type: 'entity',
                            name: 'importRecordStructure',
                            as: 'irs',
                            joinType: 'inner',
                            // Only fetch `id` — avoids pulling unnecessary columns
                            // from the bridge entity. We use `irs.id` to attach
                            // each field to its parent structure node.
                            fields: [{name: 'id'}],
                            joins: [
                                {
                                    type: 'entity',
                                    name: 'importRecord',
                                    as: 'ir',
                                    joinType: 'inner',
                                    // Only fetch `id` to avoid PGobject→String conversion
                                    // failure on `recordJson` (JSON column). Filter columns
                                    // are added automatically by QlDpEntityJdbcMembersResolver.
                                    fields: [{name: 'id'}],
                                },
                            ],
                        },
                        {
                            type: 'entity',
                            name: 'structureField',
                            as: 'sf',
                            joinType: 'inner',
                            // EiStructureField provides the human-readable name
                            // shown as the row's primary label (`sf.name`) and
                            // the raw JSON key (`sf.attributeName`) used as a
                            // fallback label. The actual value lookup in the
                            // org-chart now uses `ssf.id` (numeric
                            // EmSchemaStructure.id) because `recordJson` is a
                            // flat map keyed by that id — NOT by attributeName.
                            fields: [{name: 'id'}, {name: 'name'}, {name: 'attributeName'}],
                            joins: [
                                {
                                    type: 'entity',
                                    name: 'schemaStructureField',
                                    as: 'ssf',
                                    joinType: 'left',
                                    // ssf.id is the numeric key used inside recordJson
                                    // to resolve the field's imported value.
                                    // EiStructureField.schemaStructureField points to
                                    // an EmSchemaStructure whose id matches recordJson keys.
                                    fields: [{name: 'id'}],
                                },
                            ],
                        },
                    ],
                },
                filters: [
                    FilterCriteria.create('ir.eiImportId', FilterCriteria.cOperatorEqual, importId),
                ],
            })
            /**
             * Level 4 — EiImportRecordStructureFieldConflict rows for this import.
             * Same two-hop join pattern as the field query. `attributeName` is a
             * plain String column on the conflict entity itself, so it appears
             * as `e.attributeName` in the response (kept for the label).
             * Additionally we join `structureField` → `schemaStructureField` so the
             * org-chart can resolve the conflict's imported value from recordJson
             * via `ssf.id` (numeric EmSchemaStructure id), matching the same lookup
             * strategy used by Level-3 field nodes.
             */
            .query({
                name: 'conflicts',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'ei.EiImportRecordStructureFieldConflict',
                    as: 'e',
                    fields: [
                        {name: 'id'},
                        {name: 'attributeName'},
                        {name: 'resolutionStatus'},
                    ],
                    joins: [
                        {
                            type: 'entity',
                            name: 'importRecordStructure',
                            as: 'irs',
                            joinType: 'inner',
                            // Only fetch `id` — avoids pulling unnecessary columns
                            // from the bridge entity. We use `irs.id` to attach
                            // each conflict to its parent structure node.
                            fields: [{name: 'id'}],
                            joins: [
                                {
                                    type: 'entity',
                                    name: 'importRecord',
                                    as: 'ir',
                                    joinType: 'inner',
                                    // Only fetch `id` to avoid PGobject→String conversion
                                    // failure on `recordJson` (JSON column). Filter columns
                                    // are added automatically by QlDpEntityJdbcMembersResolver.
                                    fields: [{name: 'id'}],
                                },
                            ],
                        },
                        {
                            type: 'entity',
                            name: 'structureField',
                            as: 'sf',
                            joinType: 'inner',
                            // EiImportRecordStructureFieldConflict.structureField →
                            // EiStructureField. Only `id` needed here; the human-readable
                            // conflict label comes from `e.attributeName` above.
                            fields: [{name: 'id'}],
                            joins: [
                                {
                                    type: 'entity',
                                    name: 'schemaStructureField',
                                    as: 'ssf',
                                    joinType: 'left',
                                    // ssf.id is the numeric key used inside recordJson
                                    // to resolve the conflict's imported value. Mirrors
                                    // the same join added to the Level-3 fields query.
                                    fields: [{name: 'id'}],
                                },
                            ],
                        },
                    ],
                },
                filters: [
                    FilterCriteria.create('ir.eiImportId', FilterCriteria.cOperatorEqual, importId),
                ],
            })
            .execute()
            .subscribe({
                next: (result) => {
                    this.loading = false;
                    const records = result.get('records') ?? [];
                    const structures = result.get('structures') ?? [];
                    const fields = result.get('fields') ?? [];
                    const conflicts = result.get('conflicts') ?? [];
                    this.treeNodes = this.buildTree(records, structures, fields, conflicts);
                },
                error: (err) => {
                    this.loading = false;
                    this.errorMessage = typeof err === 'string'
                        ? err
                        : (err?.message ?? 'Laden fehlgeschlagen');
                    this.treeNodes = [];
                },
            });
    }

    /**
     * Assemble the TreeNode hierarchy:
     *  Record (root)
     *   ├── Structure (level 2, attached by importRecord id)
     *   │    ├── Structure child (via self-referential `parent` FK, recursive)
     *   │    ├── Field (level 3, attached by importRecordStructure id)
     *   │    └── Conflict (level 4, attached by importRecordStructure id)
     *   └── …
     */
    private buildTree(
        records: any[],
        structures: any[],
        fields: any[],
        conflicts: any[],
    ): TreeNode<TreeRow>[] {
        // ── 1. Record nodes (roots), keyed by id
        const recordNodes = new Map<number, TreeNode<TreeRow>>();
        const roots: TreeNode<TreeRow>[] = [];
        for (const r of records) {
            const id = this.pick(r, 'id');
            if (id == null) {
                continue;
            }
            const node: TreeNode<TreeRow> = {
                data: this.rowForRecord(id, r),
                children: [],
                expanded: false,
                leaf: false,
            };
            recordNodes.set(id, node);
            roots.push(node);
        }

        // ── 2. Structure nodes — first create all of them keyed by id, then
        //      link to parent (either another structure via `parent.id`, or the
        //      owning record via `importRecord.id`).
        const structureNodes = new Map<number, TreeNode<TreeRow>>();
        for (const s of structures) {
            const id = this.pick(s, 'id');
            if (id == null) {
                continue;
            }
            const node: TreeNode<TreeRow> = {
                data: this.rowForStructure(id, s),
                children: [],
                expanded: false,
                leaf: false,
            };
            structureNodes.set(id, node);
        }
        for (const s of structures) {
            const id = this.pick(s, 'id');
            if (id == null) {
                continue;
            }
            const node = structureNodes.get(id)!;
            // FK ids are read from the explicit join aliases (`pa.id`, `ir.id`)
            // — see the structures query above for why.
            const parentStructureId = this.pickJoinId(s, 'pa');
            if (parentStructureId != null && structureNodes.has(parentStructureId)) {
                structureNodes.get(parentStructureId)!.children!.push(node);
                continue;
            }
            const recordId = this.pickJoinId(s, 'ir');
            if (recordId != null && recordNodes.has(recordId)) {
                recordNodes.get(recordId)!.children!.push(node);
            }
        }

        // ── 3. Field nodes — attach to their structure via the `irs` join alias.
        for (const f of fields) {
            const id = this.pick(f, 'id');
            if (id == null) {
                continue;
            }
            const structureId = this.pickJoinId(f, 'irs');
            const parent = structureId != null ? structureNodes.get(structureId) : undefined;
            if (!parent) {
                continue;
            }
            parent.children!.push({
                data: this.rowForField(id, f),
                children: [],
                leaf: true,
            });
        }

        // ── 4. Conflict nodes — attach to their structure via the `irs` join alias.
        for (const c of conflicts) {
            const id = this.pick(c, 'id');
            if (id == null) {
                continue;
            }
            const structureId = this.pickJoinId(c, 'irs');
            const parent = structureId != null ? structureNodes.get(structureId) : undefined;
            if (!parent) {
                continue;
            }
            parent.children!.push({
                data: this.rowForConflict(id, c),
                children: [],
                leaf: true,
            });
        }

        // ── 5. Mark nodes with no children as leaves so PrimeNG hides the toggler.
        const markLeaves = (node: TreeNode<TreeRow>) => {
            if (!node.children || node.children.length === 0) {
                node.leaf = true;
                return;
            }
            node.children.forEach(markLeaves);
        };
        roots.forEach(markLeaves);

        return roots;
    }

    // ── TreeRow builders ──────────────────────────────────────────────────

    private rowForRecord(id: number, raw: any): TreeRow {
        const status: number | undefined = this.pick(raw, 'status');
        const requiresReview = this.pick(raw, 'requiresManualReview') === true;
        const conflictCount = Number(this.pick(raw, 'conflictCount') ?? 0);
        const resolved = Number(this.pick(raw, 'resolvedConflictCount') ?? 0);
        const openConflicts = Math.max(0, conflictCount - resolved);
        return {
            kind: 'record',
            nodeKey: `record_${id}`,
            id,
            icon: 'fa-regular fa-file-lines',
            label: `Datensatz #${id}`,
            subLabel: requiresReview ? 'Manueller Review erforderlich' : undefined,
            statusValue: status,
            statusLabel: this.labelForRecordStatus(status),
            statusSeverity: this.severityForRecordStatus(status, requiresReview),
            conflictBadge: openConflicts > 0 ? `${openConflicts} offen` : undefined,
            raw,
        };
    }

    private rowForStructure(id: number, raw: any): TreeRow {
        const assignment: number | undefined = this.pick(raw, 'assignmentStatus');
        // Read the resolved names from the explicit `st` (EiStructure) and
        // `ot` (ObjectType) join aliases. ObjectType is nullable so fall back
        // to an empty string; EiStructure is mandatory so fall back to the id.
        const objectTypeLabel = this.pickJoinField(raw, 'ot', 'name') ?? '';
        const structureLabel = this.pickJoinField(raw, 'st', 'name') ?? `Struktur #${id}`;
        const conflictCount = Number(this.pick(raw, 'conflictCount') ?? 0);
        const resolved = Number(this.pick(raw, 'resolvedConflictCount') ?? 0);
        const openConflicts = Math.max(0, conflictCount - resolved);
        return {
            kind: 'structure',
            nodeKey: `structure_${id}`,
            id,
            icon: 'fa-regular fa-sitemap',
            label: String(structureLabel),
            subLabel: objectTypeLabel ? String(objectTypeLabel) : undefined,
            statusValue: assignment,
            statusLabel: this.labelForAssignmentStatus(assignment),
            statusSeverity: this.severityForAssignmentStatus(assignment),
            conflictBadge: openConflicts > 0 ? `${openConflicts} offen` : undefined,
            raw,
        };
    }

    private rowForField(id: number, raw: any): TreeRow {
        const status: number | undefined = this.pick(raw, 'status');
        // Read the field name from the explicit `sf` (EiStructureField) join alias.
        const fieldLabel = this.pickJoinField(raw, 'sf', 'name') ?? `Feld #${id}`;
        return {
            kind: 'field',
            nodeKey: `field_${id}`,
            id,
            icon: 'fa-regular fa-tag',
            label: String(fieldLabel),
            statusValue: status,
            statusLabel: this.labelForFieldStatus(status),
            statusSeverity: this.severityForFieldStatus(status),
            raw,
        };
    }

    private rowForConflict(id: number, raw: any): TreeRow {
        const resolution: number | undefined = this.pick(raw, 'resolutionStatus');
        const attribute = this.pick(raw, 'attributeName') ?? `Konflikt #${id}`;
        return {
            kind: 'conflict',
            nodeKey: `conflict_${id}`,
            id,
            icon: 'fa-regular fa-scale-balanced',
            label: `Konflikt: ${attribute}`,
            statusValue: resolution,
            statusLabel: this.labelForResolutionStatus(resolution),
            statusSeverity: this.severityForResolutionStatus(resolution),
            raw,
        };
    }

    // ── Severity + label helpers (German UI text) ─────────────────────────

    private labelForRecordStatus(status?: number): string | undefined {
        switch (status) {
            case EiImportRecordStatusInternal.NEW:                        return 'Neu';
            case EiImportRecordStatusInternal.RUNNING:                    return 'Läuft';
            case EiImportRecordStatusInternal.WAITING_FOR_MANUAL_ACTIVITY: return 'Wartet auf Bearbeitung';
            case EiImportRecordStatusInternal.FINISHED:                   return 'Abgeschlossen';
            case EiImportRecordStatusInternal.FINISHED_NOT_RELEVANT:      return 'Nicht relevant';
            case EiImportRecordStatusInternal.FINISHED_WITHOUT_ACTIVITY:  return 'Ohne Aktion abgeschlossen';
            case EiImportRecordStatusInternal.ERROR:                      return 'Fehler';
            default:                                                       return undefined;
        }
    }

    private severityForRecordStatus(
        status: number | undefined,
        requiresReview: boolean,
    ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === EiImportRecordStatusInternal.ERROR) return 'danger';
        if (requiresReview) return 'warn';
        if (status === EiImportRecordStatusInternal.FINISHED) return 'success';
        if (status === EiImportRecordStatusInternal.FINISHED_WITHOUT_ACTIVITY) return 'success';
        if (status === EiImportRecordStatusInternal.FINISHED_NOT_RELEVANT) return 'secondary';
        if (status === EiImportRecordStatusInternal.WAITING_FOR_MANUAL_ACTIVITY) return 'warn';
        if (status === EiImportRecordStatusInternal.RUNNING) return 'info';
        if (status === EiImportRecordStatusInternal.NEW) return 'secondary';
        return 'info';
    }

    private labelForAssignmentStatus(s?: number): string | undefined {
        switch (s) {
            case EiImportRecordStructureAssignmentStatus.NOT_FOUND:          return 'Nicht gefunden';
            case EiImportRecordStructureAssignmentStatus.AUTO_FOUND:         return 'Automatisch zugeordnet';
            case EiImportRecordStructureAssignmentStatus.AUTO_CREATION:      return 'Automatisch erstellt';
            case EiImportRecordStructureAssignmentStatus.MANUAL_ASSIGNMENT:  return 'Manuell zugeordnet';
            case EiImportRecordStructureAssignmentStatus.MANUAL_CREATION:    return 'Manuell erstellt';
            case EiImportRecordStructureAssignmentStatus.QL_FOUND:           return 'QL-Treffer';
            case EiImportRecordStructureAssignmentStatus.SCOPED_FOUND:       return 'Scoped-Treffer';
            default:                                                          return undefined;
        }
    }

    private severityForAssignmentStatus(
        s: number | undefined,
    ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        switch (s) {
            case EiImportRecordStructureAssignmentStatus.AUTO_FOUND:
            case EiImportRecordStructureAssignmentStatus.QL_FOUND:
            case EiImportRecordStructureAssignmentStatus.SCOPED_FOUND:
                return 'success';
            case EiImportRecordStructureAssignmentStatus.AUTO_CREATION:
            case EiImportRecordStructureAssignmentStatus.MANUAL_ASSIGNMENT:
                return 'info';
            case EiImportRecordStructureAssignmentStatus.MANUAL_CREATION:
                return 'warn';
            case EiImportRecordStructureAssignmentStatus.NOT_FOUND:
                return 'danger';
            default:
                return 'secondary';
        }
    }

    private labelForFieldStatus(s?: number): string | undefined {
        switch (s) {
            case EiImportRecordStructureFieldStatus.DIFFERENT_SOURCE:   return 'Abweichende Quelle';
            case EiImportRecordStructureFieldStatus.DIFFERENT_DEFAULT:  return 'Abweichender Default';
            default:                                                      return undefined;
        }
    }

    private severityForFieldStatus(
        s: number | undefined,
    ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (s === EiImportRecordStructureFieldStatus.DIFFERENT_SOURCE) return 'warn';
        if (s === EiImportRecordStructureFieldStatus.DIFFERENT_DEFAULT) return 'info';
        return 'secondary';
    }

    private labelForResolutionStatus(s?: number): string | undefined {
        switch (s) {
            case EiConflictResolutionStatus.PENDING:          return 'Offen';
            case EiConflictResolutionStatus.IMPORT_ACCEPTED:  return 'Import übernommen';
            case EiConflictResolutionStatus.SYSTEM_KEPT:      return 'System behalten';
            case EiConflictResolutionStatus.MERGED:           return 'Zusammengeführt';
            case EiConflictResolutionStatus.AUTO_RESOLVED:    return 'Automatisch gelöst';
            default:                                            return undefined;
        }
    }

    private severityForResolutionStatus(
        s: number | undefined,
    ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (s === EiConflictResolutionStatus.PENDING) return 'warn';
        if (s === EiConflictResolutionStatus.AUTO_RESOLVED) return 'info';
        if (s == null) return 'secondary';
        return 'success';
    }

    // ── QL row helpers ────────────────────────────────────────────────────

    /**
     * Read a column from a QL row. The pipe emits alias-prefixed keys (`e.status`)
     * so we try the alias form first, then fall back to the bare key.
     */
    private pick(row: any, key: string): any {
        if (row == null) return undefined;
        if (row[`e.${key}`] !== undefined) return row[`e.${key}`];
        if (row[key] !== undefined) return row[key];
        return undefined;
    }

    /**
     * Read the `id` of an explicitly joined entity from a QL row. The `simple`
     * pipe emits `<joinAlias>.<column>` keys for every join, so the FK id is
     * always at `<joinAlias>.id` (regardless of the underlying FK column name).
     *
     * We use this instead of the older auto-fkey-text resolution path because
     * (a) `simple` does not expose `e.<fkName>*` keys when only the FK field is
     * listed in the projection, and (b) the auto-resolver's `fkeyt_e_<index>`
     * aliases are positional and silently break when entity FKs are reordered.
     */
    private pickJoinId(row: any, joinAlias: string): number | undefined {
        if (row == null) return undefined;
        const raw = row[`${joinAlias}.id`];
        if (raw == null) return undefined;
        if (typeof raw === 'number') return raw;
        if (typeof raw === 'string') {
            const parsed = Number(raw);
            return Number.isFinite(parsed) ? parsed : undefined;
        }
        return undefined;
    }

    /**
     * Read an arbitrary projected column of an explicitly joined entity from a
     * QL row. Used to pull resolved name labels (e.g. `st.name`, `sf.name`).
     */
    private pickJoinField(row: any, joinAlias: string, field: string): any {
        if (row == null) return undefined;
        return row[`${joinAlias}.${field}`];
    }

    // ── Template callbacks ────────────────────────────────────────────────

    /**
     * Row click handler. Emits a selection payload the parent page can route to
     * the right-hand splitter panel. We also forward the PrimeNG TreeNode so the
     * parent can render the subtree (children + grandchildren + …) via
     * p-organizationChart without re-running the QL batch.
     */
    onRowClick(row: TreeRow): void {
        if (!row) return;
        let objectType: EiTreeNodeSelection['objectType'];
        switch (row.kind) {
            case 'record':    objectType = 'ei.EiImportRecord'; break;
            case 'structure': objectType = 'ei.EiImportRecordStructure'; break;
            case 'field':     objectType = 'ei.EiImportRecordStructureField'; break;
            case 'conflict':  objectType = 'ei.EiImportRecordStructureFieldConflict'; break;
        }
        const treeNode = this.findTreeNodeByKey(row.nodeKey, this.treeNodes);
        this.nodeSelect.emit({objectType, objectId: row.id, data: row.raw, treeNode});
    }

    /**
     * Recursive lookup of a TreeNode by its `dataKey` (the unique `nodeKey` string
     * we attach to each TreeRow). Returns undefined if not found. Used by the
     * click handler so the parent can hand the exact subtree to the org-chart
     * renderer without rebuilding it.
     */
    private findTreeNodeByKey(
        nodeKey: string,
        nodes: TreeNode<TreeRow>[] | undefined,
    ): TreeNode<TreeRow> | undefined {
        if (!nodes || nodes.length === 0) return undefined;
        for (const node of nodes) {
            if (node.data?.nodeKey === nodeKey) {
                return node;
            }
            const found = this.findTreeNodeByKey(nodeKey, node.children);
            if (found) {
                return found;
            }
        }
        return undefined;
    }
}
